import {
  createRouter,
  createWebHashHistory,
  RouteRecordRaw,
  RouteLocationNormalized,
  NavigationGuardNext,
} from "vue-router";
import JwtService from "@/core/services/JwtService";
import {useUserStore} from "@/pinia/user";
import {canAccessConnectionsCrud} from "@/utils/connectionsAccess";
import {canConfigureEmailOnboarding} from "@/utils/emailOnboardingAccess";
import {parsePushNavigation, savePendingPushNavigation} from "@/services/pushNavigation.service";
import {
  canEnterPartnerRoute,
  explicitPermissionsForPartnerRoute,
  hasAllExplicitPartnerPermissions,
  hasExplicitPartnerPermission,
  permissionForPartnerRoute,
} from "@/utils/partnerPermissions";
import {canEnterCompanyFeatureRoute, companyFeatureForPartnerRoute} from "@/utils/companyFeatures";

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/dashboard",
    component: () => import("@/layout/Layout.vue"),
    meta: {requiresAuth: true},
    children: [
      {
        path: "/ana",
        name: "ana",
        component: () => import("@/views/ana/index.vue"),
      },
      {
        // Landing / guard fallback: redirects to the company's default page.
        path: "/dashboard",
        name: "dashboard",
        component: () => import("@/views/DashboardLanding.vue"),
      },
      {
        path: "/bookings",
        name: "bookings",
        component: () => import("@/views/bookings/booking-list.vue"),
      },
      {
        path: "/notifications",
        name: "push-notification",
        component: () => import("@/views/notifications/index.vue"),
        meta: {requiresAuth: true},
      },
      {
        path: "/users",
        name: "Administratori",
        component: () => import("@/views/users/users-list.vue"),
      },
      {
        path: "/drivers",
        name: "Șoferi",
        component: () => import("@/views/drivers/drivers-list.vue"),
      },
      {
        path: "/buses",
        name: "buses",
        component: () => import("@/views/buses/buses-list.vue"),
      },
      {
        path: "/buses/:id",
        name: "buses.details",
        component: () => import("@/views/buses/bus-details.vue"),
      },
      {
        path: "/platforms",
        name: "platforms.list",
        component: () => import("@/views/platforms/list.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        path: "/platforms/new",
        name: "platforms.create",
        component: () => import("@/views/platforms/create.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        path: "/platforms/:id",
        name: "platforms.item",
        component: () => import("@/views/platforms/item.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        path: "/email-accounts",
        name: "email-accounts.index",
        component: () => import("@/views/email-accounts/index.vue"),
      },
      {
        path: "/booking-sources",
        name: "company-platforms.index",
        component: () => import("@/views/company-platforms/index.vue"),
      },
      {
        // Email-booking setup wizard. The optional session id makes the setup
        // resumable from the URL (refresh, OAuth callback redirect).
        path: "/booking-sources/email-setup/:sessionId?",
        name: "email-onboarding",
        component: () => import("@/views/email-onboarding/EmailOnboardingWizard.vue"),
        meta: {requiresPartnerCompany: true, requiresCompanyAdmin: true},
      },
      {
        // Static Help center: every user of a partner company may read it;
        // articles are registered in src/views/help/helpArticles.ts. Nested
        // under one parent so the "Ajutor" menu link stays active on articles.
        path: "/help",
        component: () => import("@/views/help/HelpShell.vue"),
        children: [
          {
            path: "",
            name: "help.index",
            component: () => import("@/views/help/HelpIndex.vue"),
            meta: {requiresPartnerCompany: true},
          },
          {
            path: ":slug",
            name: "help.article",
            component: () => import("@/views/help/HelpArticle.vue"),
            meta: {requiresPartnerCompany: true},
          },
        ],
      },
      {
        path: "/companies",
        name: "companies",
        component: () => import("@/views/companies/index.vue"),
        meta: { requiresRole: ["isa"], isa: true },
      },
      {
        path: "/companies/:id",
        name: "companies.edit",
        component: () => import("@/views/companies/edit.vue"),
        meta: { requiresRole: ["isa"], isa: true },
      },
      {
        // Standalone create-booking page retired — booking creation now
        // happens exclusively through BookingCreateModal (always under the
        // Partener platform). Kept as a redirect so old links /
        // `router.push({name:'create-booking'})` fold back to the list instead
        // of 404-ing.
        path: "/create-booking",
        name: "create-booking",
        redirect: "/bookings",
      },
      // Legacy deep-links — also fold back to the bookings list.
      {
        path: "/create-booking/step1",
        redirect: "/bookings",
      },
      {
        path: "/create-booking/step2/:fromId/:toId/:dateFrom/:busId/:companyId",
        redirect: "/bookings",
      },
      {
        path: "/create-booking/step3/:fromId/:toId/:dateFrom/:busId/:companyId",
        redirect: "/bookings",
      },
      {
        path: "/create-booking/step3bs/:fromId/:toId/:dateFrom/:busId/:companyId",
        redirect: "/bookings",
      },
      {
        path: "/create-booking/step4/:fromId/:toId/:dateFrom/:busId/:companyId",
        redirect: "/bookings",
      },
      {
        path: "/bus-templates",
        name: "bus-templates",
        component: () => import("@/views/bus-templates/template-list.vue"),
      },
      {
        path: "/bus-templates/create",
        name: "bus-templates.create",
        component: () => import("@/views/bus-templates/template-create.vue"),
      },
      {
        path: "/bus-templates/:id",
        name: "bus-templates.edit",
        component: () => import("@/views/bus-templates/template-item.vue"),
      },
      {
        path: "/sms-templates",
        name: "sms-templates",
        component: () => import("@/views/sms-templates/template-list.vue"),
      },
      {
        path: "/sms-templates/create",
        name: "sms-templates.create",
        component: () => import("@/views/sms-templates/template-create.vue"),
      },
      {
        path: "/sms-templates/:id",
        name: "sms-templates.edit",
        component: () => import("@/views/sms-templates/template-item.vue"),
      },
      {
        path: "/connections/",
        name: "connections",
        component: () => import("@/views/connections/index.vue"),
        meta: { requiresConnectionsCrud: true },
      },
      {
        path: "/connections/create",
        name: "connections.create",
        component: () => import("@/views/connections/create.vue"),
        meta: { requiresConnectionsCrud: true },
      },
      {
        path: "/connections/recommendations",
        name: "connections.recommendations",
        component: () => import("@/views/connections/recommendations.vue"),
        meta: {
          requiresRole: ["isa"],
          requiresFeature: "connections_partner_crud",
          isa: true,
        },
      },
      {
        path: "/connections/:id",
        name: "connections.edit",
        component: () => import("@/views/connections/edit.vue"),
        meta: { requiresConnectionsCrud: true },
      },
      {
        path: "/routes/",
        name: "routes",
        component: () => import("@/views/routes/route-list.vue"),
      },
      {
        path: "/routes/:id",
        name: "routes.edit",
        component: () => import("@/views/routes/route-item.vue"),
      },
      {
        path: "/routes/create",
        name: "routes.create",
        component: () => import("@/views/routes/route-create.vue"),
      },
      {
        path: "/departures/",
        name: "departures",
        component: () => import("@/views/departures/departures-dashboard.vue"),
      },
      {
        path: "/price-types/",
        name: "price-types",
        component: () => import("@/views/price-types/price-types-list.vue"),
      },
      {
        path: "/price-types/create",
        name: "price-types.create",
        component: () => import("@/views/price-types/price-types-create.vue"),
      },
      {
        path: "/price-types/:id",
        name: "price-types.edit",
        component: () => import("@/views/price-types/price-types-item.vue"),
      },
      {
        path: "/properties/",
        name: "properties",
        component: () => import("@/views/properties/list.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        path: "/properties/:id",
        name: "properties.edit",
        component: () => import("@/views/properties/item.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        path: "/properties/create",
        name: "properties.create",
        component: () => import("@/views/properties/create.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        path: "/customers",
        name: "customers.list",
        component: () => import("@/views/customers/list.vue"),
      },
      {
        path: "/customers/:id",
        name: "customers.edit",
        component: () => import("@/views/customers/item.vue"),
      },
      {
        path: "/report",
        name: "report",
        component: () => import("@/views/report/index.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        // X06 — the day's dashboard (GET /reports/dashboard, `can-partner:reports.read`).
        path: "/analytics/dashboard",
        name: "analytics.dashboard",
        component: () => import("@/views/Dashboard.vue"),
        meta: {explicitPermission: "reports.read"},
      },
      {
        path: "/analytics/sales",
        name: "analytics.sales",
        component: () => import("@/views/analytics/SalesReport.vue"),
        meta: {explicitPermission: "reports.read"},
      },
      {
        path: "/analytics/occupancy",
        name: "analytics.occupancy",
        component: () => import("@/views/analytics/OccupancyReport.vue"),
        meta: {explicitPermission: "reports.read"},
      },
      {
        path: "/analytics/cash",
        name: "analytics.cash",
        component: () => import("@/views/analytics/CashReport.vue"),
        meta: {explicitPermission: "reports.read"},
      },
      {
        // X15 — punctuality from I5 disruptions; backend gate `can-partner:reports.read`.
        path: "/analytics/punctuality",
        name: "analytics.punctuality",
        component: () => import("@/views/analytics/PunctualityReport.vue"),
        meta: {explicitPermission: "reports.read"},
      },
      {
        // X06 — quality report; the XLSX button also needs `reports.export` (checked on the page).
        path: "/analytics/quality",
        name: "analytics.quality",
        component: () => import("@/views/analytics/QualityReport.vue"),
        meta: {explicitPermission: "reports.read"},
      },
      {
        // X06 — report subscriptions (X05): read = reports.read, writes = reports.export (on the page).
        path: "/analytics/subscriptions",
        name: "analytics.subscriptions",
        component: () => import("@/views/analytics/ReportSubscriptions.vue"),
        meta: {explicitPermission: "reports.read"},
      },
      {
        path: "/settlements",
        name: "settlements.index",
        component: () => import("@/views/settlements/index.vue"),
        meta: {explicitPermission: "settlements.read", requiresFeature: "settlements_f1"},
      },
      {
        path: "/settlements/settings",
        name: "settlements.settings",
        component: () => import("@/views/settlements/settings.vue"),
        meta: {explicitPermission: "settlements.manage", requiresFeature: "settlements_f1", requiresRole: ["isa"], isa: true},
      },
      {
        path: "/settlements/:id",
        name: "settlements.item",
        component: () => import("@/views/settlements/item.vue"),
        meta: {explicitPermission: "settlements.read", requiresFeature: "settlements_f1"},
      },
      {
        path: "/promotions",
        name: "promotions.index",
        component: () => import("@/views/promotions/index.vue"),
        meta: {explicitPermission: "routes.read"},
      },
      {
        path: "/promotions/create",
        name: "promotions.create",
        component: () => import("@/views/promotions/editor.vue"),
        meta: {explicitPermissions: ["routes.read", "pricing.write"]},
      },
      {
        path: "/promotions/:id(\\d+)",
        name: "promotions.item",
        component: () => import("@/views/promotions/editor.vue"),
        meta: {explicitPermission: "routes.read"},
      },
      {
        path: "/profile",
        name: "profile",
        component: () => import("@/views/profile/index.vue"),
      },
      {
        path: "/static",
        name: "static.index",
        component: () => import("@/views/static/index.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        path: "/static/create",
        name: "static.create",
        component: () => import("@/views/static/create.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        path: "/static/:id",
        name: "static.edit",
        component: () => import("@/views/static/edit.vue"),
        meta: {requiresRole: ["isa"], isa: true},
      },
      {
        path: "/legal-documents",
        name: "legal-documents.index",
        component: () => import("@/views/legal-documents/index.vue"),
        meta: { requiresRole: ["isa"], isa: true },
      },
      {
        path: "/legal-documents/create",
        name: "legal-documents.create",
        component: () => import("@/views/legal-documents/create.vue"),
        meta: { requiresRole: ["isa"], isa: true },
      },
      {
        path: "/legal-documents/:id",
        name: "legal-documents.edit",
        component: () => import("@/views/legal-documents/edit.vue"),
        meta: { requiresRole: ["isa"], isa: true },
      },
      {
        path: "/countries",
        name: "countries.index",
        component: () => import("@/views/countries/index.vue"),
      },

      {
        path: "/stations",
        name: "stations.index",
        component: () => import("@/views/stations/index.vue"),
      },
      {
        path: "/reviews",
        name: "reviews.index",
        component: () => import("@/views/reviews/index.vue"),
        meta: {requiresRole: ["isa", "iaa"], isa: true, iaa: true},
      },
      {
        path: "/settings",
        name: "settings.index",
        component: () => import("@/views/settings/index.vue"),
        meta: { requiresPartnerCompany: true },
      },
      {
        // X13 — company message templates. `settings.write` comes from the
        // `/settings` prefix (permissionForPartnerRoute). No
        // `requiresPartnerCompany`: a super administrator picks the company
        // on the page (`?company=<id>`).
        path: "/settings/message-templates",
        name: "settings.message-templates",
        component: () => import("@/views/settings/message-templates.vue"),
      },
      {
        // X02 — scheduled manifest delivery (X01 API). Outside the
        // `/settings` prefix on purpose: reading needs `manifest.export` only
        // (an operator sees a read-only screen); creating, editing and
        // deleting also need `settings.write`, enforced by the page and the
        // API. No `requiresPartnerCompany`: a super administrator picks the
        // company on the page (`?company=<id>`).
        path: "/manifest-schedule",
        name: "manifest-schedule",
        component: () => import("@/views/manifest-schedule/ManifestScheduleSettings.vue"),
        meta: {explicitPermission: "manifest.export"},
      },
      {
        path: "/billing",
        name: "billing.index",
        component: () => import("@/views/billing/index.vue"),
        meta: { requiresPartnerCompany: true },
      },
      {
        path: "/sms-history",
        name: "sms-history.index",
        component: () => import("@/views/sms-history/index.vue"),
      },
      {
        path: "/emails-history",
        name: "emails-history.index",
        component: () => import("@/views/emails-history/index.vue"),
      },
      {
        path: "/emails/:bookingId?",
        name: "emails.index",
        component: () => import("@/views/emails/index.vue"),
      },
      {
        path: "/bs-destinations",
        name: "bs-destinations.index",
        component: () => import("@/views/bs-destinations/index.vue"),
        meta: { requiresRole: ["isa", "iaa"], requiresAdmin: true, isa: true, iaa: true },
      },
      {
        path: "/wgo",
        name: "wgo.index",
        component: () => import("@/views/wgo/index.vue"),
        meta: { requiresRole: ["isa", "iaa"], requiresAdmin: true, isa: true, iaa: true },
      },
      {
        path: "/refund-policies",
        name: "refund-policies.list",
        component: () => import("@/views/refund-policies/list.vue"),
        meta: { requiresRole: ["isa", "iaa"], isa: true, iaa: true },
      },
      {
        path: "/refund-policies/create",
        name: "refund-policies.create",
        component: () => import("@/views/refund-policies/create.vue"),
        meta: { requiresRole: ["isa", "iaa"], isa: true, iaa: true },
      },
      {
        path: "/refund-policies/:id",
        name: "refund-policies.edit",
        component: () => import("@/views/refund-policies/item.vue"),
        meta: { requiresRole: ["isa", "iaa"], isa: true, iaa: true },
      },
      {
        path: "/ai-settings",
        name: "ai-settings",
        component: () => import("@/views/ai-settings/index.vue"),
        meta: { requiresRole: ["isa"], isa: true },
      },
      {
        path: "/security-center",
        name: "security-center",
        component: () => import("@/views/security-center/index.vue"),
        meta: { requiresRole: ["isa"], isa: true },
      },
      {
        path: "/duplicate-phones",
        name: "duplicate-phones",
        component: () => import("@/views/duplicate-phones/index.vue"),
        meta: { requiresRole: ["isa"], isa: true, preserveScrollOnQueryChange: true },
      },
      {
        path: "/integration-health",
        name: "integration-health",
        component: () => import("@/views/integration-health/index.vue"),
        meta: { requiresRole: ["isa"], isa: true },
        children: [{
          path: ":integration",
          name: "integration-health.detail",
          component: () => import("@/views/integration-health/detail.vue"),
          meta: { requiresRole: ["isa"], isa: true },
        }],
      },
      {
        path: "/payment-operations",
        name: "payment-operations.index",
        component: () => import("@/views/payment-operations/index.vue"),
        meta: { requiresRole: ["isa", "iaa"], requiresAdmin: true, isa: true, iaa: true },
      },

    ],
  },
  {
    path: "/",
    component: () => import("@/components/page-layouts/Auth.vue"),
    children: [
      {
        path: "/sign-in",
        name: "sign-in",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignIn.vue"),
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/components/page-layouts/Driver.vue"),
    children: [
      {
        path: "/driver",
        name: "driver",
        component: () =>
          import("@/views/driver/index.vue"),
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/dashboard",
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// The navigation guard is extracted (verbatim, no behavior change) into a named,
// exported function so it can be executed directly in tests against a mocked
// user store — instead of asserting on the router source as a string. It is
// still wired into `router.beforeEach` below exactly as before.
export async function routeGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const userStore = useUserStore();
  const token = JwtService.getToken();

  if (to.name === "push-notification") {
    const ids = parsePushNavigation(to.query as Record<string, unknown>);
    if (ids) savePendingPushNavigation(ids);
  }
  if (to.name === "push-notification" && !token) {
    next({name: "sign-in"});
    return;
  }

  if (token) await userStore.verifyToken({api_token: token});

  // For gated routes, always await current verification to prevent stale decisions
  const requiredFeature = to.meta?.requiresFeature as string | undefined;
  const requiresAuth = !!to.meta?.requiresAuth;
  const requiresConnectionsCrud = !!to.meta?.requiresConnectionsCrud;
  const requiresCompanyAccess = !!to.meta?.requiresCompanyAccess;
  const requiresPartnerCompany = !!to.meta?.requiresPartnerCompany;
  const requiresCompanyAdmin = !!to.meta?.requiresCompanyAdmin;
  const requiredRoles = (to.meta?.requiresRole as string[] | undefined) ?? [];
  // Mapped legacy routes run in shadow mode (backend logs, never blocks);
  // only enforced routes can redirect (see canEnterPartnerRoute).
  const requiredPermission = permissionForPartnerRoute(to.path);
  const explicitPermission = to.meta?.explicitPermission as string | undefined;
  const explicitPermissions = [
    ...((to.meta?.explicitPermissions as string[] | undefined) ?? []),
    ...explicitPermissionsForPartnerRoute(to.path),
  ];
  // Per-company feature flags ("funcții noi în testare"): new pages stay closed
  // for a company without the feature (utils/companyFeatures).
  const requiredCompanyFeature = companyFeatureForPartnerRoute(to.path);

  if (
    requiresAuth
    || requiredFeature
    || requiresConnectionsCrud
    || requiresCompanyAccess
    || requiresPartnerCompany
    || requiresCompanyAdmin
    || requiredRoles.length
    || requiredPermission
    || explicitPermission
    || explicitPermissions.length
    || requiredCompanyFeature
  ) {
    if (userStore._verifyPromise) {
      try {
        await userStore._verifyPromise;
      } catch (_verifyError) {
        // Verify threw unexpectedly; store catch handler already ran logout
      }
    }

    // Explicit failure path: verify failed or no token — redirect to sign-in
    if (!userStore.isAuthenticated) {
      next({name: 'sign-in'});
      return;
    }

    if (requiredPermission && !canEnterPartnerRoute(userStore.user, to.path)) {
      next({name: 'dashboard'});
      return;
    }


    if (explicitPermission && !hasExplicitPartnerPermission(userStore.user, explicitPermission)) {
      next({name: 'dashboard'});
      return;
    }

    if (explicitPermissions.length && !hasAllExplicitPartnerPermissions(userStore.user, explicitPermissions)) {
      next({name: 'dashboard'});
      return;
    }

    // The landing (/dashboard → bookings | departures) is never feature-gated,
    // so a company without the feature folds back to its default page.
    if (requiredCompanyFeature && !canEnterCompanyFeatureRoute(userStore.user, to.path)) {
      next({name: 'dashboard'});
      return;
    }

    // Feature check against resolved (not stale) capabilities
    if (requiredFeature && userStore.user?.features?.[requiredFeature] !== true) {
      next({name: 'dashboard'});
      return;
    }

    // The backend resolves the complete Connections CRUD capability from the
    // rollout flag, admin role and canonical company scope. Missing capability
    // data fails closed here.
    if (requiresConnectionsCrud && !canAccessConnectionsCrud(userStore.user)) {
      next({name: 'dashboard'});
      return;
    }

    // Scope check: user must have company access
    if (requiresCompanyAccess && !userStore.user?.company_access_list?.length) {
      next({name: 'dashboard'});
      return;
    }

    // Partner-company check: the backend 404s for admins without an acting
    // partner company (Autocare admins, whose `company` is "" per the partner
    // API). Keep them off pages they cannot load.
    if (requiresPartnerCompany && !userStore.user?.company) {
      next({name: 'dashboard'});
      return;
    }

    // Company-administrator check (email setup wizard): an operator of the
    // same company must not reach configuration pages.
    if (requiresCompanyAdmin && !canConfigureEmailOnboarding(userStore.user)) {
      next({name: 'company-platforms.index'});
      return;
    }

    // Role check: user must have at least one of the required roles (e.g. isa || iaa)
    if (requiredRoles.length) {
      const userRecord = (userStore.user ?? {}) as Record<string, unknown>;
      const hasRole = requiredRoles.some((role) => !!userRecord[role]);
      if (!hasRole) {
        next({name: 'dashboard'});
        return;
      }
    }
  }

  if (!(to.meta?.preserveScrollOnQueryChange && to.path === from.path)) {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }

  next();
}

router.beforeEach(routeGuard);
router.afterEach(() => window.dispatchEvent(new Event("partner:pwa-route-changed")));

export default router;
